// Phase MHM-1 → MHM-6 — top sub-nav for the MHM portal.
// MHM-5: Settings is now an active tab; a theme toggle button (light/dark/system)
// sits on the right side of the sub-nav.
// MHM-6: Inbound Parcels tab + BUILD_STAMP must match VersionWatcher /
//        MicroLeadSubNav / PackerSubNav. Bump these four together.
import { NavLink } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import {
  LayoutDashboard, Package, Building2, Users, Truck, MapPin, IndianRupee,
  LifeBuoy, Boxes, BarChart3, PackageSearch, Settings, Sun, Moon, Monitor,
  ClipboardCheck, FileQuestion,
  Inbox,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

const BUILD_STAMP = "APP-1.0-r7";

const ACTIVE_TABS = [
  { to: "/dashboard/hub/main",       label: "Dashboard",  Icon: LayoutDashboard, testid: "mhm-tab-dashboard" },
  { to: "/dashboard/hub/inbound",    label: "Inbound",    Icon: Inbox,           testid: "mhm-tab-inbound" },
  { to: "/dashboard/hub/orders",     label: "Orders",     Icon: Package,         testid: "mhm-tab-orders" },
  { to: "/dashboard/hub/dispatch",   label: "Dispatch",   Icon: Truck,           testid: "mhm-tab-dispatch" },
  { to: "/dashboard/hub/live-map",   label: "Live Map",   Icon: MapPin,          testid: "mhm-tab-livemap" },
  { to: "/dashboard/hub/micro-hubs", label: "Micro Hubs", Icon: Building2,       testid: "mhm-tab-micro-hubs" },
  { to: "/dashboard/hub/agents",     label: "Agents",     Icon: Users,           testid: "mhm-tab-agents" },
  { to: "/dashboard/hub/packer-approvals", label: "Packer Approvals", Icon: ClipboardCheck, testid: "mhm-tab-packer-approvals" },
  { to: "/dashboard/hub/my-suggestions",   label: "My Suggestions",   Icon: FileQuestion,   testid: "mhm-tab-my-suggestions" },
  { to: "/dashboard/hub/finance",    label: "Finance",    Icon: IndianRupee,     testid: "mhm-tab-finance" },
  { to: "/dashboard/hub/support",    label: "Support",    Icon: LifeBuoy,        testid: "mhm-tab-support" },
  { to: "/dashboard/hub/inventory",  label: "Inventory",  Icon: Boxes,           testid: "mhm-tab-inventory" },
  { to: "/dashboard/hub/analytics",  label: "Analytics",  Icon: BarChart3,       testid: "mhm-tab-analytics" },
  { to: "/dashboard/hub/couriers",   label: "Couriers",   Icon: PackageSearch,   testid: "mhm-tab-couriers" },
  { to: "/dashboard/hub/courier-shipments", label: "Shipments", Icon: PackageSearch, testid: "mhm-tab-courier-shipments" },
  { to: "/dashboard/hub/settings",   label: "Settings",   Icon: Settings,        testid: "mhm-tab-settings" },
];

const THEME_META = {
  light:  { Icon: Sun,     label: "Light"   },
  dark:   { Icon: Moon,    label: "Dark"    },
  system: { Icon: Monitor, label: "System"  },
};

function ThemeToggle() {
  const { theme, cycle, effective } = useTheme();
  const { Icon, label } = THEME_META[theme] || THEME_META.system;
  return (
    <button onClick={cycle} type="button"
            data-testid="mhm-theme-toggle"
            data-theme-state={theme}
            data-theme-effective={effective}
            title={`Theme: ${label} (click to cycle)`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs
                       bg-slate-100 hover:bg-slate-200 text-slate-700
                       dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200
                       border border-transparent dark:border-slate-700 transition-colors">
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

export default function MHMSubNav() {
  const user = useAuthStore((s) => s.user);
  const mainHubId = user?.scope?.main_hub_id || user?.hub_id;
  // HUB-DRILL-1: include a "My Main Hub" shortcut deep-linking to the
  // MHM's own drill-down full page.
  const tabs = mainHubId
    ? [
        { to: `/dashboard/founder/main-hubs/${mainHubId}`,
          label: "My Main Hub", Icon: Building2,
          testid: "mhm-tab-my-main-hub" },
        ...ACTIVE_TABS,
      ]
    : ACTIVE_TABS;
  return (
    <div className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 backdrop-blur sticky top-0 z-30"
         data-testid="mhm-subnav">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mr-3">
          Hub portal
        </div>
        {tabs.map(({ to, label, Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ` +
              (isActive
                ? "bg-[#FF6B00] text-white shadow-sm"
                : "bg-slate-100 hover:bg-slate-200 text-slate-700 " +
                  "dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200")
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500"
                data-testid="build-stamp">{BUILD_STAMP}</span>
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}
